import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { DocChronologyDownloadColumn } from '../../../../backend/src/matter/subset/chronology/doc-chronology/doc-chronology-download';
import { StyleFormat, DownloadFormat, DocumentDownloadOptions } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface UseDocChronologyDownloadProps {
  docChronologyId: string;
  matterId: string;
  subsetId: string;
}

interface ExportResponse {
  exportId: string;
  jobId: string;
}

const useDocChronologyDownload = ({ docChronologyId, matterId, subsetId }: UseDocChronologyDownloadProps) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadDocChronology = async ({
    styleFormat,
    title,
    columns,
    relativityColumns,
    style,
    downloadFormat,
    documentOptions,
    includeSourceDocuments = false,
    orderByDate,
  }: {
    styleFormat: StyleFormat;
    downloadFormat: DownloadFormat;
    title: string;
    columns?: DocChronologyDownloadColumn[];
    relativityColumns?: string[];
    style?: string;
    documentOptions?: DocumentDownloadOptions;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }): Promise<ExportResponse> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        downloadFormat,
        styleFormat,
        title,
        columns,
        relativityColumns,
        style,
        documentOptions,
        orderByDate,
      },
    });

    const endpoint = includeSourceDocuments
      ? `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/download-with-documents`
      : `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/download`;

    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.message ?? 'Failed to start document chronology export');
    }

    return await response.json();
  };

  const downloadExport = async (title: string, exportId: string, format?: DownloadFormat): Promise<void> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.GET,
    });

    const formatParam = format ? `&format=${format}` : '';
    const endpoint = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/export-download-url?exportId=${exportId}${formatParam}`;
    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      throw new Error('Failed to get document chronology export download URL');
    }

    const data: { downloadUrl: string } = await response.json();

    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = format ? `${title}.${format}` : `${title} with source documents.zip`;
    link.click();
  };

  return {
    downloadMutation: useMutation(downloadDocChronology, {
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to download document chronology. Please try again.');
      },
    }),
    downloadExport,
  };
};

export default useDocChronologyDownload;
